import type {ExecutionResult} from 'graphql';

export type SuccessResult = {
  success: true;
  linkResult: ExecutionResult;
  extensions: {containsNonNullableFields: boolean};
};

export type FailedResult = {
  success: false;
  error: unknown;
};

function findFailedRequest(
  results: ReadonlyArray<SuccessResult | FailedResult>,
): FailedResult | undefined {
  return results.find((result) => !result.success);
}

export type StitchedResult =
  | {
      success: true;
      result: ExecutionResult;
    }
  | {
      success: false;
      error: unknown;
    };

// This function stitches the results of multiple requests into a single result.
// It also nullifies the whole data in two cases:
// 1) Any of the sublinks containing non-nullable fields return GraphQL errors.
// 2) Any of the sublinks returns a network, 5xx or a client error
// This is to mimic the future behavior of a GraphQL gateway.
export default function stitchSubLinksResults(
  results: ReadonlyArray<SuccessResult | FailedResult>,
): StitchedResult {
  const failedRequest = findFailedRequest(results);

  if (failedRequest) {
    return {
      success: false,
      error: failedRequest.error,
    };
  }

  let resultData: ExecutionResult['data'] = {};
  let resultErrors: NonNullable<ExecutionResult['errors']> = [];
  let shouldNullifyData = false;

  results.forEach((result) => {
    if (!result.success) {
      // This shouldn't happen since we already checked for failed requests
      // but it's needed to make TypeScript happy
      return;
    }

    const {linkResult, extensions} = result;
    const {data, errors} = linkResult;

    if (data) {
      resultData = {...resultData, ...data};
    }

    if (errors) {
      resultErrors = [...resultErrors, ...errors];

      const containsNonNullableFields = extensions?.containsNonNullableFields;

      if (containsNonNullableFields) {
        shouldNullifyData = true;
      }
    }
  });

  const result = {
    data: shouldNullifyData ? null : resultData,
    errors: resultErrors.length > 0 ? resultErrors : undefined,
  };

  return {
    success: true,
    result,
  };
}
